import {
  objectURLResponseHandler,
  revokeObjectURLOptions,
} from "@/shared/lib/utilities/object-url";
import { backendApi } from "@/services/backend/api";
import { updateCache } from "@/services/backend/_lib/update-cache";
import {
  ReferenceNumberFilters,
  ResetReferenceNumberRequest,
} from "@/services/backend/documents/types/reference-number";

import {
  Documentation,
  Item,
} from "@/services/backend/rlt/work-orders/types/documentation";
import { Kind } from "@/services/backend/rlt/treatments/types/component";
import { Filter } from "@/shared/lib/filter/filter";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { DocumentState } from "@/services/backend/documents/types/state";

const documentationApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    rltDocumentationCreate: build.mutation<Documentation, CreateRequest>({
      query: (request) => ({
        url: `/rlt/documentation`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: [{ type: "RltDocumentation", id: "LIST" }],
    }),
    rltDocumentationShow: build.query<Documentation, ShowRequest>({
      query: (request) => ({
        url: `/rlt/documentation`,
        method: "GET",
        params: request,
      }),
      providesTags: (result) =>
        result ? [{ type: "RltDocumentation", id: result.id }] : [],
    }),
    rltDocumentationPatch: build.mutation<Documentation, PatchRequest>({
      query: (request) => ({
        url: `/rlt/documentation`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _error, request) => [
        { type: "RltDocumentation", id: request.id },
        { type: "RltDocumentation", id: "LIST" },
      ],
    }),
    rltDocumentationResetReferenceNumber: build.mutation<
      Documentation,
      ResetReferenceNumberRequest
    >({
      query: (request) => ({
        url: `/rlt/documentation/reset-reference-number`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltDocumentation", id: request.id },
        { type: "RltDocumentation", id: "LIST" },
      ],
    }),
    rltDocumentationList: build.query<List, ListRequest>({
      query: (request) => ({
        url: `/rlt/documentation/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) =>
        result ? [{ type: "RltDocumentation", id: "LIST" }] : [],
    }),
    rltDocumentationAddItem: build.mutation<Documentation, AddItemRequest>({
      query: (request) => ({
        url: `/rlt/documentation/item`,
        method: "POST",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltDocumentation", id: request.id },
      ],
    }),
    rltDocumentUpdateItem: build.mutation<Documentation, UpdateItemRequest>({
      query: (request) => ({
        url: `/rlt/documentation/item`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltDocumentation", id: request.id },
      ],
    }),
    rltDocumentationDeleteItem: build.mutation<null, DeleteItemRequest>({
      query: (request) => ({
        url: `/rlt/documentation/item`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltDocumentation", id: request.id },
      ],
    }),
    rltDocumentationUploadImage: build.mutation<Item, UploadImageRequest>({
      query: (request) => {
        const body = new FormData();
        body.append("id", request.id);
        body.append("itemId", request.itemId);
        body.append("beforeOrAfter", request.beforeOrAfter);
        body.append("imageId", request.imageId);
        body.append("image", request.image, request.image.name);
        return {
          url: `/rlt/documentation/item/image`,
          method: "POST",
          headers: {
            //  this is needed, otherwise a 415 error is returned
            Accept: "application/json",
          },
          body,
          formData: true,
        };
      },
      invalidatesTags: (_result, _e, request) => [
        { type: "RltDocumentation", id: request.itemId },
        "RltDocumentation",
      ],
    }),
    rltDocumentationUploadImageForPosition: build.mutation<
      Item,
      UploadImageForPositionRequest
    >({
      query: (request) => {
        const body = new FormData();
        body.append("id", request.id);
        body.append("positionId", request.positionId);
        body.append("positionNumber", request.positionNumber);
        body.append("componentKind", request.componentKind);
        body.append("beforeOrAfter", request.beforeOrAfter);
        body.append("imageId", request.imageId);
        body.append("image", request.image, request.image.name);
        return {
          url: `/rlt/documentation/item/image-for-position`,
          method: "POST",
          headers: {
            //  this is needed, otherwise a 415 error is returned
            Accept: "application/json",
          },
          body,
          formData: true,
        };
      },
      invalidatesTags: (result) => [
        { type: "RltDocumentation", id: result?.id },
        "RltDocumentation",
      ],
    }),
    rltDocumentationShowImage: build.query<
      { objectURL: string },
      ShowImageRequest
    >({
      ...revokeObjectURLOptions(),
      query: (request) => ({
        url: `/rlt/documentation/item/image`,
        method: "GET",
        params: request,
        responseHandler: objectURLResponseHandler,
        // Keep the image out of the browser's HTTP cache.
        cache: "no-store",
      }),
    }),
    rltDocumentationDeleteImage: build.mutation<Item, DeleteImageRequest>({
      query: (request) => ({
        url: `/rlt/documentation/item/image`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltDocumentation", id: request.id },
      ],
    }),
    rltDocumentationDelete: build.mutation<null, DeleteRequest>({
      query: (request) => ({
        url: `/rlt/documentation`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "RltDocumentation", id: request.id },
        { type: "RltDocumentation", id: "LIST" },
      ],
    }),
  }),
});

export const {
  useRltDocumentationCreateMutation,
  useRltDocumentationShowQuery,
  useRltDocumentationPatchMutation,
  useRltDocumentationResetReferenceNumberMutation,
  useRltDocumentationListQuery,
  useRltDocumentationAddItemMutation,
  useRltDocumentUpdateItemMutation,
  useRltDocumentationDeleteItemMutation,
  useRltDocumentationUploadImageMutation,
  useRltDocumentationUploadImageForPositionMutation,
  useRltDocumentationShowImageQuery,
  useRltDocumentationDeleteImageMutation,
  useRltDocumentationDeleteMutation,
} = documentationApi;

// PatchRequest updates the document. All fields except `id` are optional:
// an omitted key leaves the corresponding value unchanged.
export interface PatchRequest {
  id: string;
  referenceNumber?: string;
}

export interface CreateRequest {
  id: string;
  processId: string;
  companyId: string;
  branchId: string;
  customerId: string;
  serviceObjectId: string;
}

export interface ShowRequest {
  id: string;
}

export interface List {
  documentations: Documentation[];
  count: number;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters, ReferenceNumberFilters {
  orderBy?: OrdrBy<string>[];
  state?: Filter<DocumentState>;
  limit?: number;
  offset?: number;
}

export interface AddItemRequest {
  id: string;
  itemId: string;
}

export interface UpdateItemRequest {
  id: string;
  itemId: string;
  positionId: string;
  title: string;
  description: string;
  isInternal: boolean;
}

export interface DeleteItemRequest {
  id: string;
  itemId: string;
}

export interface UploadImageRequest {
  id: string;
  itemId: string;
  beforeOrAfter: "before" | "after";
  imageId: string;
  image: File;
}

export interface UploadImageForPositionRequest {
  id: string;
  positionId: string;
  positionNumber: string;
  componentKind: Kind;
  beforeOrAfter: "before" | "after";
  imageId: string;
  image: File;
}

export interface ShowImageRequest {
  id: string;
  imageId: string;
}

export interface DeleteImageRequest {
  id: string;
  itemId: string;
  imageId: string;
}

export interface DeleteRequest {
  id: string;
}

export const updateRltDocumentationCache = updateCache(
  documentationApi,
  "rltDocumentationShow",
);
